import { service } from "@ember/service";
import Controller from "@ember/controller";
import { isPresent, isNone } from "@ember/utils";
import { action } from "@ember/object";
import { task, keepLatestTask } from "ember-concurrency";
import generatePassword from "glesys-controlpanel/utils/generate-password";
import { trackedObject } from "@ember/reactive/collections";
import { tracked } from "@glimmer/tracking";
import ENV from "glesys-controlpanel/config/environment";
import { dataCenters } from "glesys-controlpanel/models/data-center";
import { saveRecord } from "@warp-drive/legacy/compat/builders";

const DEFAULT_PLATFORM_TEMPLATES = {
  VMware: "Windows Server 2025 Standard LTSC",
  KVM: "Ubuntu 26.04",
};

export default class ServerCreateVirtualController extends Controller {
  @service ajax;
  @service http;
  @service intl;
  @service session;
  @service router;
  @service store;
  @service abilities;

  get project() {
    return this.model.project;
  }

  get platformArguments() {
    return this.model.platformArguments;
  }

  @tracked hostname;
  description;
  @tracked password = "";

  @tracked users;

  @tracked selectedPlatform;
  @tracked selectedTemplate;
  @tracked selectedDataCenter;
  @tracked selectedDiskSize;
  @tracked selectedMemorySize;
  @tracked selectedCpuCores;
  @tracked selectedBandwidthOption;
  @tracked selectedIpVersion4;
  @tracked selectedIpVersion6;
  @tracked campaignCode;
  @tracked selectedGpuType;

  @tracked availablePlans;

  @tracked selectedPlan = trackedObject({
    type: "flexible",
    plan: null,
    size: null,
  });

  get placeholderCloudConfig() {
    return this.isWindowsTemplate
      ? "## template: glesys\n#cloud-config\n{{> windowsUsers }}"
      : "## template: glesys\n#cloud-config\n{{> users }}\nbootcmd:\n  - echo 'Hello {{users.0.username}}!'";
  }

  @tracked _cloudConfig;

  get cloudConfig() {
    return this._cloudConfig && this._cloudConfig !== this.placeholderCloudConfig
      ? this._cloudConfig
      : this.placeholderCloudConfig;
  }

  set cloudConfig(value) {
    this._cloudConfig = value;
  }

  @tracked serverCost;

  tabs;

  backupSchedules;

  get templateArguments() {
    let templateArguments = this.model.serverTemplates.filter(
      (template) => template.platform === this.selectedPlatform,
    );

    if (this.selectedPlan?.plan && this.selectedPlan?.size) {
      templateArguments = this.selectedPlan.plan.templates.filter(
        (template) =>
          template.minimumDiskSize <= this.selectedPlan.size.disksize &&
          template.minimumMemorySize <= this.selectedPlan.size.memorysize,
      );
    }

    let noneTemplate = templateArguments?.find((templateArgument) => templateArgument.value === "None");
    if (isPresent(noneTemplate)) {
      return templateArguments.filter((template) => template !== noneTemplate).concat(noneTemplate);
    }
    return templateArguments;
  }

  get currentlySelectedTemplateOrDefaultTemplateForPlatform() {
    let selectedTemplate = this.templateArguments.find((x) => x == this.selectedTemplate);
    let defaultTemplateForPlatform = this.templateArguments.find((x) =>
      `${x.value}`.startsWith(DEFAULT_PLATFORM_TEMPLATES[this.selectedPlatform]),
    );
    let fallbackTemplate = this.templateArguments[0];

    return selectedTemplate || defaultTemplateForPlatform || fallbackTemplate;
  }

  get currentlySelectedDataCenterOrDefaultDataCenter() {
    let selectedDataCenter = this.selectedPlatformArguments.dataCenters.find((x) => x.value == this.selectedDataCenter);
    return selectedDataCenter?.value || "Falkenberg";
  }

  get hasSelectedNoneTemplate() {
    return this.selectedTemplate.value === "None";
  }

  get isWindowsTemplate() {
    let template = this.selectedTemplate.value;
    return `${template}`.toLowerCase().indexOf("windows") > -1;
  }

  get filteredUsers() {
    return this.users.filter((user) => user.username || user.sshKey || user.password);
  }

  get hasResellerPricing() {
    return this.abilities.cannot("access pricing for organization", this.project?.organization);
  }

  get canNotSubmit() {
    if (this.project.organization.isRestricted) {
      return true;
    }

    if (this.abilities.cannot("manage for project", this.project)) {
      return true;
    }

    if (!this.hostname) {
      return true;
    }

    if (this.hasSelectedNoneTemplate) {
      return false;
    }

    if (this.isFixedPlan) {
      return !this.selectedPlan.size;
    }

    if (this.supportsCloudInit) {
      if (this.hasCustomCloudConfig) {
        return false;
      }

      if (!this.supportsUser) {
        return false;
      }

      return (
        this.filteredUsers.length === 0 ||
        !this.filteredUsers.every((user) => user.username && (user.sshKey || user.password))
      );
    } else {
      return !this.password;
    }
  }

  get hasCustomCloudConfig() {
    return this.placeholderCloudConfig !== this.cloudConfig;
  }

  get supportsCloudInit() {
    return this.selectedTemplate.bootstrapMethod === "CLOUD_INIT";
  }

  get supportsUser() {
    return this.supportsCloudInit && !this.isTalos && !this.isOpnsense;
  }

  get isKvm() {
    return this.selectedPlatform === "KVM";
  }

  get isTalos() {
    return this.selectedTemplate.value.toLowerCase().startsWith("talos");
  }

  get isOpnsense() {
    return this.selectedTemplate.value.toLowerCase().startsWith("opnsense");
  }

  get pricePerMonth() {
    let cost = 0;

    let diskSize = this.selectedDiskSize;
    let memorySize = this.selectedMemorySize;
    let cpuCores = this.selectedCpuCores;
    let bandwidthOption = this.selectedBandwidthOption;
    let template = this.selectedTemplate;

    if (diskSize && memorySize && cpuCores && bandwidthOption && template) {
      cost += diskSize.basecost + diskSize.licensecost;
      cost += template.basecost + template.licensecost;
      cost += memorySize.basecost + memorySize.licensecost;
      cost += cpuCores.basecost + cpuCores.licensecost;
      cost += bandwidthOption.basecost + bandwidthOption.licensecost;
    }

    return cost.toFixed(2);
  }

  get diskSizeOptions() {
    let diskSizes = this.selectedPlatformArguments.diskSizes;
    if (!this.selectedTemplate) {
      return diskSizes;
    }

    return diskSizes.filter((option) => {
      return option.value >= this.selectedTemplate.minimumDiskSize;
    });
  }

  get memorySizeOptions() {
    let memorySizes = this.selectedPlatformArguments.memorySizes;
    if (!this.selectedTemplate) {
      return memorySizes;
    }

    return memorySizes.filter((option) => {
      return option.value >= this.selectedTemplate.minimumMemorySize;
    });
  }

  get dataCenterOptions() {
    let options = this.selectedPlatformArguments.dataCenters;

    if (this.isFixedPlan) {
      return options.filter(
        (option) => dataCenters.find((dataCenter) => dataCenter.name === option.value)?.hasFixedPlanSupport,
      );
    }

    return options;
  }

  get selectedPlatformArguments() {
    let projectId = this.project.id;
    let platformArguments = this.platformArguments;
    let selectedPlatform = this.selectedPlatform;
    let id = `${projectId}-${selectedPlatform}`;
    if (platformArguments) {
      return platformArguments.find((platformArgument) => platformArgument.id === id);
    }
    return null;
  }

  get supportsFixedPlans() {
    return this.isKvm;
  }

  get isFlexibleResources() {
    return this.selectedPlan.type === "flexible";
  }

  get isFixedPlan() {
    return this.selectedPlan.type === "fixed";
  }

  loadPlansTask = task(async () => {
    let {
      content: { plans },
    } = await this.http.request({
      url: `${ENV.apiBaseUri}/server/listplans?projectkey=${this.project.id}`,
    });

    this.availablePlans = plans.map((plan) => ({
      ...plan,
      sizes: plan.sizes?.slice().sort((a, b) => a.cpucores - b.cpucores || a.memorysize - b.memorysize),
      templates: plan.templates.map((template) => ({
        ...template,
        templateId: template.id,
        value: template.name,
        bootstrapMethod: template.bootstrapmethod,
        supportsCloudInitSshKeys: template.supportscloudinitsshkeys,
        minimumDiskSize: template.minimumdisksize,
        minimumMemorySize: template.minimummemorysize,
      })),
    }));
  });

  getKVMServerCostTask = keepLatestTask(async () => {
    if (this.hasResellerPricing) {
      return;
    }
    let url = `${ENV.apiBaseUri}/server/estimatedcost`;

    let numberOfBackupImagesFromSchedules = this.backupSchedules
      .filter((schedule) => schedule.enabled)
      .reduce((totalPrice, schedule) => totalPrice + schedule.numberofimagestokeep, 0);

    let data = {
      projectkey: this.project?.id,
      bandwidth: this.selectedBandwidthOption?.value,
      numberofbackupimagestokeep: numberOfBackupImagesFromSchedules,
      campaigncode: this.campaignCode?.length >= 5 ? this.campaignCode : undefined,
      templateid: this.selectedTemplate?.templateId,
      gputypeid: this.selectedGpuType || null,
    };

    if (this.isFixedPlan) {
      data.plansizekey = this.selectedPlan.size?.key;
    } else {
      data.cpucores = this.selectedCpuCores?.value;
      data.disksize = this.selectedDiskSize?.value;
      data.memorysize = this.selectedMemorySize?.value;
    }

    try {
      let response = await this.ajax.post(url, { data });

      this.serverCost = {
        price: response.billing.price,
        total: response.billing.total,
      };
    } catch {
      this.serverCost = {
        price: null,
      };
    }
  });

  createServerTask = task(async () => {
    let project = this.project;
    let properties = {
      project,
      hostname: this.hostname,
      description: this.description,
      platform: this.selectedPlatform,
      templatename: this.selectedTemplate.value,
      datacenter: this.selectedDataCenter,
      bandwidth: this.selectedBandwidthOption.value,
      campaigncode: this.campaignCode,
    };

    if (this.isFixedPlan) {
      properties.plansizekey = this.selectedPlan.size?.key;
    } else {
      properties.disksize = this.selectedDiskSize.value;
      properties.memorysize = this.selectedMemorySize.value;
      properties.cpucores = this.selectedCpuCores.value;
    }

    if (this.supportsCloudInit) {
      if (this.supportsUser) {
        let users = this.filteredUsers.map((user) => {
          let object = { username: user.username };

          this.selectedTemplate.supportsCloudInitSshKeys && user.sshKey && (object.sshkeys = [user.sshKey]);
          user.password && (object.password = user.password);

          return object;
        });

        properties.users = users;
      }

      if (this.hasCustomCloudConfig) {
        properties.cloudconfig = this.cloudConfig;
      }

      if (this.isOpnsense) {
        properties.cloudconfig = "## just an empty cloudconfig since API requires it without users";
      }
    } else if (!this.hasSelectedNoneTemplate) {
      properties.rootpassword = this.password;
    }

    if (this.isKvm) {
      properties.backupschedules = this.backupSchedules
        .filter((schedule) => schedule.enabled)
        .map(({ frequency, numberofimagestokeep }) => ({ frequency, numberofimagestokeep }));

      properties.gputypeid = this.selectedGpuType || null;
    }

    properties.ip = this.selectedIpVersion4?.id ?? "none";
    properties.ipv6 = this.selectedIpVersion6?.id ?? "none";

    let server = this.store.createRecord("server", properties);
    await this.store.request(saveRecord(server)).catch((error) => {
      server.unloadRecord();
      throw error;
    });

    this.resetProperties();
    this.setDefaultValues();

    this.router.transitionTo("server.details", server.id);
  });

  setTabs() {
    this.tabs = [
      { name: this.intl.t("servers.create.tabs.distribution"), route: "servers.create.virtual" },
      { name: this.intl.t("servers.create.tabs.from-backup"), route: "servers.create.from-backup" },
      { name: this.intl.t("servers.create.tabs.one-click-installer"), route: "servers.appliance" },
    ];
  }

  setDefaultValues() {
    let setDefaultValueIfNone = (propertyName, argumentName, defaultValue) => {
      if (isNone(this[propertyName])) {
        let platformArguments = this.selectedPlatformArguments;
        this[propertyName] = platformArguments
          .get(argumentName)
          .find((platformArgument) => platformArgument.value === defaultValue);
      }
    };

    setDefaultValueIfNone("selectedBandwidthOption", "externalBandwidthOptions", 100);
    setDefaultValueIfNone("selectedCpuCores", "cpuCores", 2);
    setDefaultValueIfNone("selectedDiskSize", "diskSizes", 20);
    setDefaultValueIfNone("selectedMemorySize", "memorySizes", 2048);
  }

  resetProperties() {
    this.hostname = null;
    this.password = "";
    this.users = [
      trackedObject({
        username: this.session.get("currentUser")?.defaultServerUsername,
        password: "",
        sshKey: "",
      }),
    ];

    this.selectedPlatform = "KVM";

    this.selectedTemplate = this.currentlySelectedTemplateOrDefaultTemplateForPlatform;
    this.selectedDataCenter = "Falkenberg";

    this.selectedPlan = trackedObject({
      type: "flexible",
      plan: null,
      size: null,
    });

    this.selectedBandwidthOption = null;
    this.selectedCpuCores = null;
    this.selectedDiskSize = null;
    this.selectedMemorySize = null;

    this.selectedIpVersion4 = null;
    this.selectedIpVersion6 = null;

    this.campaignCode = null;
    this.backupSchedules = [];
    this.selectedGpuType = null;
  }

  onChange = (data) => {
    this.hostname = data.hostname;
    if ("campaignCode" in data && data.campaignCode !== this.campaignCode) {
      let previousCampaignCode = this.campaignCode;
      this.campaignCode = data.campaignCode;
      if (this.isKvm && (data.campaignCode?.length >= 5 || previousCampaignCode?.length >= 5)) {
        this.getKVMServerCostTask.perform();
      }
    }
  };

  @action
  onPasswordChange(event) {
    this.password = event.target.value;
  }

  @action
  generatePassword() {
    this.password = generatePassword();
  }

  @action
  updateBackup(backupSchedules) {
    this.backupSchedules = backupSchedules;
    this.getKVMServerCostTask.perform();
  }

  @action
  onPlatformChange(platform) {
    this.selectedPlatform = platform;
    this.selectedPlan = trackedObject({
      type: "flexible",
      plan: null,
      size: null,
    });
    this.selectedDataCenter = this.currentlySelectedDataCenterOrDefaultDataCenter;
    this.selectedTemplate = this.currentlySelectedTemplateOrDefaultTemplateForPlatform;
    this.campaignCode = null;
  }

  @action
  addUser() {
    let newUser = trackedObject({
      username: "",
      password: "",
      sshKey: "",
    });

    this.users = [...this.users, newUser];
  }

  @action
  removeUser(user) {
    this.users = this.users.filter((u) => u !== user);
  }

  @action
  onUserChange(user, username, password, sshKey) {
    user.username = username;
    user.password = password;
    user.sshKey = sshKey;

    this.users = [...this.users];
  }

  @action
  onDiskSizeChange(diskSize) {
    this.selectedDiskSize = diskSize;
    if (this.isKvm) {
      this.getKVMServerCostTask.perform();
    }
  }

  @action
  onMemorySizeChange(memorySize) {
    this.selectedMemorySize = memorySize;
    if (this.isKvm) {
      this.getKVMServerCostTask.perform();
    }
  }

  @action
  onCpuCoresChange(cpuCores) {
    this.selectedCpuCores = cpuCores;
    if (this.isKvm) {
      this.getKVMServerCostTask.perform();
    }
  }

  @action
  onBandwidthOptionChange(bandwidthOption) {
    this.selectedBandwidthOption = bandwidthOption;
    if (this.isKvm) {
      this.getKVMServerCostTask.perform();
    }
  }

  @action
  selectTemplate(event) {
    this.selectedTemplate = this.templateArguments.find((template) => template.value === event.target.value);
    if (this.isKvm) {
      this.getKVMServerCostTask.perform();
    }
  }

  @action
  selectDataCenter(event) {
    this.selectedDataCenter = event.target.value;
    this.selectedTemplate = this.currentlySelectedTemplateOrDefaultTemplateForPlatform;
    this.campaignCode = null;
    if (this.selectedGpuType) {
      this.selectedGpuType = null;
      this.getKVMServerCostTask.perform();
    }
  }

  @action
  onIpsChange(IPv4, IPv6) {
    this.selectedIpVersion4 = IPv4;
    this.selectedIpVersion6 = IPv6;
  }

  @action
  onCloudConfigChange(cloudConfig) {
    this.cloudConfig = cloudConfig;
  }

  @action
  onGpuChange(gpuType) {
    this.selectedGpuType = gpuType;
    this.getKVMServerCostTask.perform();
  }

  onPlanTypeChange = (type) => {
    this.selectedPlan.type = type;

    if (type === "fixed") {
      this.selectedGpuType = null;
      this.selectedDataCenter = this.dataCenterOptions[0]?.value;
      this.selectedPlan.plan = this.availablePlans.at(0);
      this.selectedPlan.size = this.selectedPlan.plan.sizes.at(0);
    } else {
      this.selectedPlan.plan = null;
      this.selectedPlan.size = null;
    }

    this.selectedTemplate = this.currentlySelectedTemplateOrDefaultTemplateForPlatform;
    this.getKVMServerCostTask.perform();
  };

  onPlanChange = (plan) => {
    this.selectedPlan.plan = plan;
    this.selectedPlan.size = plan.sizes.at(0);

    this.selectedTemplate = this.currentlySelectedTemplateOrDefaultTemplateForPlatform;
    this.getKVMServerCostTask.perform();
  };

  onPlanSizeChange = (planSize) => {
    this.selectedPlan.size = planSize;

    this.selectedTemplate = this.currentlySelectedTemplateOrDefaultTemplateForPlatform;
    this.getKVMServerCostTask.perform();
  };
}
